import type { DailyGoal } from '@oefy/contracts';
import { Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cx } from '../ui/styles';
import { copy } from './copy';

function useStarsSeen(childId: string, completed: number): number {
  const key = `oefy:stars:${childId}:${new Date().toDateString()}`;
  const [seen] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(key);
      return stored === null ? completed : Number(stored);
    } catch {
      return completed;
    }
  });
  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, String(completed));
    } catch {
      return;
    }
  }, [key, completed]);
  return seen;
}

export function GoalStars({
  childId,
  goal,
}: {
  readonly childId: string;
  readonly goal: DailyGoal;
}) {
  const seen = useStarsSeen(childId, goal.completed);
  const label = copy.dailyGoal(goal.completed, goal.target);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="flex items-center gap-0.5 border-l border-slate-200 pl-2.5"
    >
      {Array.from({ length: goal.target }, (_, index) => (
        <Star
          key={index}
          aria-hidden="true"
          style={{ animationDelay: `${String((index - seen) * 260 + 200)}ms` }}
          strokeWidth={2.2}
          className={cx(
            'size-6',
            index < goal.completed ? 'fill-amber-400 text-amber-500' : 'fill-white text-amber-300',
            index >= seen && index < goal.completed ? 'motion-safe:animate-star-land' : null,
          )}
        />
      ))}
    </span>
  );
}
